/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, ReactElement } from 'react';
import { useAsyncCallback, UseAsyncReturn } from 'react-async-hook';
import { isNumber } from 'lodash';
import { fmtPercent } from '@upstart/harmony/lib/formatter';
import {
  isApproved,
  isConditional,
  isDenied,
  isCounter,
  isUpstartOffer as evaluateIsUpstartOffer,
} from '@upstart/harmony/lib/response-utils';
import { FinanceProvider } from '@upstart/harmony/lib/constants';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import { getFinanceResponseTotalAPR } from '@upstart/harmony/lib/payment-utils';
import { getImageForFinanceSource } from '@upstart/harmony/lib/metadata';
import {
  FinanceApplication,
  FinanceResponse,
  UpstartApplicationStatus,
} from '@upstart/harmony/lib/legos/financingTypes';
import {
  LendingOfferTile,
  PlaceholderTile,
  DeniedTile,
  LendingStatusBadgeEnum,
  PLACEHOLDER_MINUTE_WARNING,
  PLACEHOLDER_WAITING_FOR_OFFERS,
  LendingValueBadgeEnum,
} from '@upstart/desking-components';
import { getUpstartOfferEventProperties, sendData } from 'util/amplitude';
import PaymentsService from '../../services/PaymentsService';
import DuplicateUpstartOfferButton from './DuplicateOfferButton/DuplicateOfferButton';
import { amplitudeEventsDeskingCTR } from '../../util/amplitudeEvents';
import {
  getLendingValueBadgeEnum,
  isApprovedCounter,
} from '../../util/financeResponseUtils';

// share this type elsewhere since it is copied from LendingOfferDetail.tsx
interface FinanceResponseWithOverrides extends FinanceResponse {
  selected?: boolean;
  overrides?: {
    markup?: number;
    monthly?: number;
  };
}

interface LendingOfferProps {
  application?: FinanceApplication;
  response: FinanceResponse;
  applicationStatus?: UpstartApplicationStatus;
  pencilResponse?: FinanceResponseWithOverrides;
  setResponse: Function;
  setDetailViewOffer: React.Dispatch<
    React.SetStateAction<FinanceResponse | null | undefined>
  >;
  detailViewOffer: FinanceResponse | null | undefined;
  showUpstartPlaceholder?: boolean;
  dealer?: DealerWithFlags;
  deal?: Deal;
  asyncCloneFinanceResponse: UseAsyncReturn<FinanceResponse, any[]>;
  duplicateUpstartOfferLimitReached: boolean;
  forceExpandOfferList: () => void;
  height?: number;
}

function LendingOffer({
  application,
  response,
  applicationStatus,
  pencilResponse,
  setResponse,
  setDetailViewOffer,
  detailViewOffer,
  showUpstartPlaceholder,
  deal,
  dealer,
  asyncCloneFinanceResponse,
  duplicateUpstartOfferLimitReached,
  forceExpandOfferList,
  height,
}: LendingOfferProps) {
  const isSelected = pencilResponse?.selected ?? false;
  const isHighlighted = response.id === detailViewOffer?.id;
  const apr = response.data?.apr;
  const months = response.data?.months;
  const responseOverrides = pencilResponse?.overrides;
  const markupOverride = responseOverrides?.markup;
  const hasMarkupOverride = isNumber(markupOverride);

  const isUpstartOffer =
    !!response && evaluateIsUpstartOffer(response, application);
  const displayMarkup =
    !isUpstartOffer && isNumber(apr) && application?.paymentDataId;
  const financeSourceImgName = isUpstartOffer
    ? application?.provider
    : response.financeSourceName;

  const fetchPayments = useAsyncCallback(
    PaymentsService.getPaymentsForFinanceResponse,
  );
  const isEditable =
    isApproved(response) ||
    isConditional(response) ||
    fetchPayments.loading ||
    isApprovedCounter(response);
  const [markup, setMarkup] = useState(
    hasMarkupOverride ? markupOverride : response?.data?.markup || 0,
  );
  const [isInvalid, setIsInvalid] = useState(false);

  // Flag for when the offer is calculated to be out of bounds of any approved rate
  // Used to show status indicator to click on tile - only show if not denied
  const isInvalidUpstartOffer =
    isUpstartOffer && isApproved(response) && isInvalid;

  // calculate monthly payment
  const monthly = hasMarkupOverride
    ? responseOverrides?.monthly
    : response.data?.monthly;
  const finalMonthlyAmount = response?.data?.monthlyOverride ?? monthly;

  // calculate apr
  let finalAPR = apr;
  if (response?.data?.aprOverride) {
    finalAPR = response?.data?.aprOverride;
  } else if (hasMarkupOverride) {
    finalAPR = getFinanceResponseTotalAPR(pencilResponse);
  }

  useEffect(() => {
    // Check for the most up to date backend amount to validate selected offer
    const reloadNormalizedResponse = async () => {
      if (response && isUpstartOffer) {
        const hasBackendCalculationVersion = !!response.data
          ?.backendCalculationVersion;
        const responseWithPayments: FinanceResponse = await fetchPayments.execute(
          response,
          {
            includeValidations: true,
            includeBackendAmount: hasBackendCalculationVersion,
            withRICDefaults: hasBackendCalculationVersion,
          },
        );
        setIsInvalid(!!responseWithPayments.validations?.isInvalid);
      }
    };
    reloadNormalizedResponse();
  }, [response, application]);

  useEffect(() => {
    if (isSelected && isUpstartOffer && isInvalid) {
      // Make sure offer is not selected
      setResponse({ ...pencilResponse, selected: false });
    }
  }, [isSelected, isUpstartOffer, isInvalid]);

  useEffect(() => {
    setMarkup(hasMarkupOverride ? markupOverride : response?.data?.markup || 0);
    // eslint-disable-next-line
  }, [pencilResponse]);

  // This is a catch in case we render an approved Upstart offer and the loading tile at the same time.
  if (isUpstartOffer && showUpstartPlaceholder) {
    return null;
  }

  const valueBadge = getLendingValueBadgeEnum(response, isInvalid);
  let statusBadge = isCounter(response)
    ? LendingStatusBadgeEnum.APPROVED_COUNTER
    : LendingStatusBadgeEnum.APPROVED;

  if (
    statusBadge === LendingStatusBadgeEnum.APPROVED &&
    valueBadge &&
    [
      LendingValueBadgeEnum.HIGHER_DOWN_PAYMENT_LOWER_APR,
      LendingValueBadgeEnum.LONGER_TERM_LOWER_APR,
      LendingValueBadgeEnum.SHORTER_TERM_LOWER_APR,
    ].includes(valueBadge)
  ) {
    statusBadge = LendingStatusBadgeEnum.RECOMMENDED;
  }

  const applicationStatusDisabled =
    !!applicationStatus?.signedOn || !!applicationStatus?.voidedOn;

  const disableCheckbox =
    !isEditable || isInvalidUpstartOffer || applicationStatusDisabled;

  const lendingOfferTileOnClick = () => {
    setDetailViewOffer(pencilResponse);
    sendData(
      amplitudeEventsDeskingCTR.FINANCE_OFFER_TILE_CLICKED,
      getUpstartOfferEventProperties({
        dealId: deal?.id,
        application,
        response,
        aprOverride: finalAPR,
      }),
    );
  };

  let tile: ReactElement;
  if (isUpstartOffer && fetchPayments.loading) {
    // This particular placeholder tile is shown when the Upstart Offer has been updated and payments are being fetched.
    tile = (
      <PlaceholderTile
        financeSourceName={FinanceProvider.UPSTART}
        financeImageUrl={getImageForFinanceSource(FinanceProvider.UPSTART)}
        rowOne={PLACEHOLDER_MINUTE_WARNING}
        rowTwo={PLACEHOLDER_WAITING_FOR_OFFERS}
        height={height}
      />
    );
  } else if (isDenied(response)) {
    tile = (
      <DeniedTile
        financeSourceName={financeSourceImgName}
        financeImageUrl={
          financeSourceImgName
            ? getImageForFinanceSource(financeSourceImgName)
            : undefined
        }
        message="See Offer details for reasoning"
        height={height}
        onClick={lendingOfferTileOnClick}
        isSelected={isHighlighted}
      />
    );
  } else {
    tile = (
      <LendingOfferTile
        statusBadge={statusBadge}
        valueBadge={valueBadge}
        financeSourceName={financeSourceImgName}
        financeImageUrl={
          financeSourceImgName
            ? getImageForFinanceSource(financeSourceImgName)
            : undefined
        }
        apr={finalAPR}
        term={months}
        monthlyPayment={finalMonthlyAmount}
        downPayment={response.data?.downPayment}
        height={height}
        isSelected={isHighlighted}
        checked={isSelected && !isInvalidUpstartOffer}
        onCheckboxToggle={() => {
          setResponse({ ...pencilResponse, selected: !isSelected });
        }}
        onClick={lendingOfferTileOnClick}
        checkboxDisabled={disableCheckbox}
      />
    );
  }

  // render
  return (
    <div>
      {tile}
      {isUpstartOffer && !isDenied(response) && dealer && (
        <div className="flex justify-end">
          <DuplicateUpstartOfferButton
            financeResponse={response}
            isUpstartOffer={isUpstartOffer}
            isInvalid={isInvalid}
            disabled={
              duplicateUpstartOfferLimitReached || applicationStatusDisabled
            }
            asyncCloneFinanceResponse={asyncCloneFinanceResponse}
            dealer={dealer}
            forceExpandOfferList={forceExpandOfferList}
            amplitudeOnclickAction={() => {
              sendData(
                amplitudeEventsDeskingCTR.DUPLICATE_OFFER_BUTTON_CLICKED,
                getUpstartOfferEventProperties({
                  dealId: deal?.id,
                  application,
                  response,
                }),
              );
            }}
          />
        </div>
      )}
      {!isUpstartOffer && displayMarkup && (
        <div className="flex justify-end">
          <div className="lending-markup pt-5">
            <div>Markup: {fmtPercent(markup)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LendingOffer;
